import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import type { ApiEnvelope } from '@etherworks/shared-types';
import { useAuth } from './AuthContext';

export interface UsageInfo {
  count: number;
  limit: number;
  remaining: number;
  tokenCount: number;
  tokenLimit: number;
  tokenRemaining: number;
  resetsAt: string;
  tier?: string;
}

interface UsageContextType {
  usageInfo: UsageInfo | null;
  refreshUsage: () => Promise<void>;
}

const UsageContext = createContext<UsageContextType | undefined>(undefined);

export function useUsage() {
  const context = useContext(UsageContext);
  if (context === undefined) {
    throw new Error('useUsage must be used within a UsageProvider');
  }
  return context;
}

export const UsageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [usageInfo, setUsageInfo] = useState<UsageInfo | null>(null);

  const fetchUsage = useCallback(async () => {
    try {
      const response = await axios.get<ApiEnvelope<UsageInfo>>('/api/billing/usage');
      if (response.data.code === 0 && response.data.data) {
        setUsageInfo(response.data.data);
      }
    } catch {
      // Silently ignore — user may not be authenticated yet
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setUsageInfo(null);
      return;
    }

    void fetchUsage();
  }, [user, fetchUsage]);

  return (
    <UsageContext.Provider value={{ usageInfo, refreshUsage: fetchUsage }}>
      {children}
    </UsageContext.Provider>
  );
};
